import {SignalType} from 'gabi-api-ts/v2/common/signals/signals';
import {Patient} from 'gabi-api-ts/v2/patient/query/patient_query';
import {SignalQueryServiceClient} from 'gabi-api-ts/v2/signal/query/signal_query.client';
import {useTranslation} from 'react-i18next';

import {HealthReportStatisticsPerDay} from '@/components/business/analytics/health-report/health-report-statistics-per-day';
import {HealthReportStatisticsTrend} from '@/components/business/analytics/health-report/health-report-statistics-trend';
import {NoDataCell} from '@/components/business/analytics/patient-overview-cells/no-data-cell';
import {PatientsActiveTableCellHoverable} from '@/components/business/analytics/patients/patients-active-table-cell-hoverable';
import ErrorBoundary from '@/components/errors/error-boundary';
import ColoredContainer from '@/components/layout/colored-container';
import {MiniLoadingView} from '@/components/static/mini-loading-view';
import {useBackendQuery} from '@/hooks/use-backend-query';
import {SignalDefinitions} from '@/model/signal-definitions/signal-definition';
import {transformAverageSummary} from '@/model/transformers/transform-average-summary';
import {useDebugFlags} from '@/services/debug-service';
import {formatJSDateToApiDateTs} from '@/util/apidate-util';
import {subDays} from '@/util/apidate-util';
import {NewDateUtil} from '@/util/new-date-util';
import {getSignalTypeUnit} from '@/util/signal-type-util';

type AverageCellProps = {
    patient: Patient;
    signalType: SignalType;
    handleWidgetChartClick: (selectedDate: Date) => void;
}

function AverageCell({patient, signalType, handleWidgetChartClick}: AverageCellProps) {
    const { t } = useTranslation();
    const debugFlags = useDebugFlags();
    const [averageSummaryLoading, averageSummary] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getSignalAverageSummary,
        memoize: true,
        data: {
            signalType: signalType,
            patientId: patient.patientId,
            date: patient.lastRecordingDate,
            nbPreviousDays: 14,
        },
        transformer: transformAverageSummary,
    });

    const signalColors = (SignalDefinitions[signalType] ?? SignalDefinitions[-1]).colors;

    if (averageSummaryLoading || !averageSummary) {
        return (
            <PatientsActiveTableCellHoverable
                hasHover={false}
                hoverElement={() => null}
            >
                <div className="text-center">
                    <MiniLoadingView />
                </div>
            </PatientsActiveTableCellHoverable>
        );
    }
    else {
        const today = NewDateUtil();
        return (
            <PatientsActiveTableCellHoverable
                hasHover={debugFlags.alwaysShowTrendsOverlays || (averageSummary.hasAverageValue && !averageSummary.hasNormalTrend)}
                hoverElement={() => (
                    <div className={'warning-overlay animate__animated animate__fadeIn animate__faster'}>
                        <ErrorBoundary>
                            <ColoredContainer color={signalColors.normal}>
                                {!averageSummary.hasTrend && (
                                    <p className="trendsNoData">{t('pages.patients.trends.noData')}</p>
                                )}
                                {averageSummary.hasTrend && (
                                    <HealthReportStatisticsPerDay
                                        patientId={patient.patientId!.id}
                                        signalType={signalType}
                                        lastRecordingDate={patient.lastRecordingDate!}
                                        dateFrom={subDays(patient.lastRecordingDate!, 14)}
                                        dateTo={formatJSDateToApiDateTs(today)}
                                        ignoreUnifiedDuration
                                        displayPreviousNightsAverage={true}
                                        onClick={handleWidgetChartClick}
                                    />
                                )}
                            </ColoredContainer>
                        </ErrorBoundary>
                    </div>
                )}
            >
                {(!averageSummary.hasAverageValue || !averageSummary.averageValue) ? (
                    <NoDataCell/>
                ) : (<>
                    <strong>{Math.round(averageSummary.averageValue?.value ?? 0)}</strong>
                    {' '}
                    {getSignalTypeUnit(signalType)}
                    {averageSummary.trend !== undefined && (
                        <HealthReportStatisticsTrend
                            trend={averageSummary.trend}
                        />
                    )}
                </>)}
            </PatientsActiveTableCellHoverable>
        );
    }
}

export {AverageCell};
